import { useCallback, useEffect, useMemo, useState } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import debounce from 'lodash.debounce'

import { QueryVariables } from './usePaginatedStays'

export default function useStayTable() {
  const [search, setSearch] = useState<string>('')
  const [states, setStates] = useState<string[]>([])
  const [sortDirection, setSortDirection] = useState<GraphQL.SortDirection>(
    GraphQL.SortDirection.Asc
  )
  const [sortField, setSortField] = useState<GraphQL.StaySortField>(
    GraphQL.StaySortField.ScheduledStart
  )

  const filterStates = useMemo(() => {
    return (states.length === 0 ? null : states) as GraphQL.StayStates[] | null
  }, [states])

  const [variables, setVariables] = useState<QueryVariables>(() => {
    return {
      filter: {
        search,
        states: filterStates
      },
      sort: {
        direction: sortDirection,
        field: sortField
      }
    }
  })

  const updateSort = useCallback(
    (field: GraphQL.StaySortField, direction: GraphQL.SortDirection) => {
      setSortField(field)
      setSortDirection(direction)
    },
    [setSortField, setSortDirection]
  )

  const debounceSearch = useMemo(() => {
    return debounce(
      (search: string) =>
        setVariables(vars => ({
          ...vars,
          filter: {
            ...vars.filter,
            search
          }
        })),
      1000
    )
  }, [setVariables])

  useEffect(() => {
    if (variables.filter.search === search) return

    debounceSearch(search)
  }, [variables, search, debounceSearch])

  useEffect(() => {
    const currentStates = variables.filter.states

    const hasSameStates =
      filterStates === currentStates ||
      (filterStates?.length === currentStates?.length &&
        filterStates?.join(',') === currentStates?.join(','))

    if (hasSameStates) return

    setVariables(variables => ({
      ...variables,
      filter: {
        ...variables.filter,
        states: filterStates
      }
    }))
  }, [variables, filterStates])

  useEffect(() => {
    if (
      variables.sort.direction === sortDirection &&
      variables.sort.field === sortField
    ) {
      return
    }

    setVariables(variables => ({
      ...variables,
      sort: {
        direction: sortDirection,
        field: sortField
      }
    }))
  }, [sortDirection, sortField, variables, setVariables])

  return {
    queryVariables: variables,
    search,
    states,
    setSearch,
    setStates,
    updateSort
  }
}
